import React, { useState, useEffect } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { supabase, testSupabaseConnection, checkTableExists } from '@/lib/supabase';
import { useAccount } from '@/contexts/AccountContext';
import { Loader2, Plus, Trash2, Edit2, Save, X, Wallet, Users, Building2 } from "lucide-react";
import WalletBalance from '@/components/wallets/WalletBalance';
import { useGamification } from '@/contexts/GamificationContext';
import WalletTypeSelection from '@/components/wallets/WalletTypeSelection';

interface Wallet {
  id: string;
  name: string;
  balance: number;
  is_active: boolean;
  type: string;
}

const Wallets = () => {
  const { toast } = useToast();
  const { accountType, setAccountType } = useAccount();
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [loading, setLoading] = useState(true);
  const [newWalletName, setNewWalletName] = useState('');
  const [newWalletBalance, setNewWalletBalance] = useState('0');
  const [isAddingWallet, setIsAddingWallet] = useState(false);
  const [editingWallet, setEditingWallet] = useState<Wallet | null>(null);
  const [editName, setEditName] = useState('');
  const [editBalance, setEditBalance] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { addXp, checkAchievements } = useGamification();

  const loadWallets = async () => {
    try {
      setLoading(true);
      setError(null);

      // Test Supabase connection first
      const isConnected = await testSupabaseConnection();
      if (!isConnected) {
        setError('Unable to connect to the database. Please check your internet connection.');
        return;
      }

      // Check if wallets table exists
      const walletsExist = await checkTableExists('wallets');
      if (!walletsExist) {
        setError('Wallets table not found. Please check your database setup.');
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError("Please sign in to view your wallets");
        return;
      }

      console.log('Loading wallets for user:', user.id, 'account type:', accountType);

      const { data, error } = await supabase
        .from('wallets')
        .select('*')
        .eq('user_id', user.id)
        .eq('type', accountType);

      if (error) {
        console.error('Error loading wallets:', error);
        setError(`Failed to load wallets: ${error.message}`);
        return;
      }

      console.log('Loaded wallets:', data?.length || 0);
      setWallets(data || []);
    } catch (error) {
      console.error('Error loading wallets:', error);
      setError("Failed to load wallets. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accountType) {
      loadWallets();
    }
  }, [accountType]);

  const addWallet = async () => {
    if (!newWalletName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a wallet name",
        variant: "destructive",
      });
      return;
    }

    const initialBalance = parseFloat(newWalletBalance);
    if (isNaN(initialBalance)) {
      toast({
        title: "Error",
        description: "Please enter a valid balance amount",
        variant: "destructive",
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Error",
          description: "Please sign in to add a wallet",
          variant: "destructive",
        });
        return;
      }

      if (!accountType) {
        toast({
          title: "Error",
          description: "Please select an account type first",
          variant: "destructive",
        });
        return;
      }

      // Ensure accountType is one of the allowed values
      const validTypes = ['personal', 'family', 'business'];
      if (!validTypes.includes(accountType.toLowerCase())) {
        toast({
          title: "Error",
          description: "Invalid account type. Please select a valid account type.",
          variant: "destructive",
        });
        return;
      }

      console.log('Adding wallet for user:', user.id, 'account type:', accountType);

      const { data, error } = await supabase
        .from('wallets')
        .insert([
          {
            user_id: user.id,
            name: newWalletName.trim(),
            type: accountType.toLowerCase(),
            balance: initialBalance,
            is_active: true
          }
        ])
        .select()
        .single();

      if (error) {
        console.error('Error adding wallet:', error);
        toast({
          title: "Error",
          description: `Failed to add wallet: ${error.message}`,
          variant: "destructive",
        });
        return;
      }

      console.log('Added wallet:', data);
      setWallets([...wallets, data]);
      setNewWalletName('');
      setNewWalletBalance('0');
      setIsAddingWallet(false);
      
      // Force reload wallets to update the graph
      loadWallets();
      
      // Add XP for creating a new wallet
      await addXp(50);
      // Check for achievements
      await checkAchievements();

      toast({
        title: "Success",
        description: "Wallet added successfully",
      });
    } catch (error) {
      console.error('Error adding wallet:', error);
      toast({
        title: "Error",
        description: "Failed to add wallet. Please try again.",
        variant: "destructive",
      });
    }
  };

  const deleteWallet = async (walletId: string) => {
    if (!confirm('Are you sure you want to delete this wallet? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('wallets')
        .delete()
        .eq('id', walletId);

      if (error) {
        console.error('Error deleting wallet:', error);
        toast({
          title: "Error",
          description: `Failed to delete wallet: ${error.message}`,
          variant: "destructive",
        });
        return;
      }

      setWallets(wallets.filter(w => w.id !== walletId));
      
      // Add XP for managing wallets
      await addXp(10);
      // Check for achievements
      await checkAchievements();

      toast({
        title: "Success",
        description: "Wallet deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting wallet:', error);
      toast({
        title: "Error",
        description: "Failed to delete wallet. Please try again.",
        variant: "destructive",
      });
    }
  };

  const toggleActiveWallet = async (walletId: string) => {
    try {
      const { error } = await supabase
        .from('wallets')
        .update({ is_active: !wallets.find(w => w.id === walletId)?.is_active })
        .eq('id', walletId);

      if (error) {
        console.error('Error updating wallet:', error);
        toast({
          title: "Error",
          description: `Failed to update wallet: ${error.message}`,
          variant: "destructive",
        });
        return;
      }

      setWallets(wallets.map(w => 
        w.id === walletId ? { ...w, is_active: !w.is_active } : w
      ));
      
      // Add XP for managing wallet status
      await addXp(15);
      // Check for achievements
      await checkAchievements();

      toast({
        title: "Success",
        description: "Wallet status updated successfully",
      });
    } catch (error) {
      console.error('Error updating wallet:', error);
      toast({
        title: "Error",
        description: "Failed to update wallet. Please try again.",
        variant: "destructive",
      });
    }
  };

  const startEditingWallet = (wallet: Wallet) => {
    setEditingWallet(wallet);
    setEditName(wallet.name);
    setEditBalance(wallet.balance.toString());
  };

  const saveWalletEdit = async () => {
    if (!editingWallet) return;

    try {
      const newBalance = parseFloat(editBalance);
      if (isNaN(newBalance)) {
        toast({
          title: "Error",
          description: "Please enter a valid balance amount",
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase
        .from('wallets')
        .update({
          name: editName.trim(),
          balance: newBalance
        })
        .eq('id', editingWallet.id);

      if (error) {
        console.error('Error updating wallet:', error);
        toast({
          title: "Error",
          description: `Failed to update wallet: ${error.message}`,
          variant: "destructive",
        });
        return;
      }

      setWallets(wallets.map(w => 
        w.id === editingWallet.id 
          ? { ...w, name: editName.trim(), balance: newBalance }
          : w
      ));
      setEditingWallet(null);
      
      // Add XP for updating a wallet
      await addXp(25);
      // Check for achievements
      await checkAchievements();

      toast({
        title: "Success",
        description: "Wallet updated successfully",
      });
    } catch (error) {
      console.error('Error updating wallet:', error);
      toast({
        title: "Error",
        description: "Failed to update wallet. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 py-8">
        {error}
        <Button onClick={loadWallets} className="mt-4">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Wallets</h1>
        <Button onClick={() => setIsAddingWallet(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Wallet
        </Button>
      </div>

      {/* Wallet List by Type */}
      <div className="space-y-8">
        {/* Personal Wallets */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Personal Wallets</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {wallets
              .filter(wallet => wallet.type === 'personal')
              .map((wallet) => (
                <Card key={wallet.id} className="p-4 sm:p-6 hover:shadow-lg transition-shadow">
                  <div className="flex flex-col h-full">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-primary/10 rounded-full">
                            <Wallet className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-medium text-lg">{wallet.name}</h3>
                            <p className="text-sm text-muted-foreground">Personal Wallet</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {editingWallet?.id !== wallet.id && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => startEditingWallet(wallet)}
                                className="h-8 w-8"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => deleteWallet(wallet.id)}
                                className="h-8 w-8 text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {editingWallet?.id === wallet.id ? (
                        <div className="space-y-3">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="Wallet name"
                            className="w-full"
                          />
                          <Input
                            type="number"
                            value={editBalance}
                            onChange={(e) => setEditBalance(e.target.value)}
                            placeholder="Balance"
                            className="w-full"
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={saveWalletEdit} className="flex-1">
                              <Save className="h-4 w-4 mr-1" />
                              Save
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingWallet(null)} className="flex-1">
                              <X className="h-4 w-4 mr-1" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-2xl font-bold">
                            Rp {wallet.balance.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                          <div className="flex items-center gap-2">
                            <div className={`h-2 w-2 rounded-full ${wallet.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                            <p className="text-sm text-muted-foreground">
                              {wallet.is_active ? 'Active' : 'Inactive'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {editingWallet?.id !== wallet.id && (
                      <div className="mt-4 pt-4 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleActiveWallet(wallet.id)}
                          className="w-full"
                        >
                          {wallet.is_active ? 'Deactivate' : 'Activate'} Wallet
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
          </div>
        </div>

        {/* Family Wallets */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Family Wallets</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {wallets
              .filter(wallet => wallet.type === 'family')
              .map((wallet) => (
                <Card key={wallet.id} className="p-4 sm:p-6 hover:shadow-lg transition-shadow">
                  <div className="flex flex-col h-full">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-primary/10 rounded-full">
                            <Users className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-medium text-lg">{wallet.name}</h3>
                            <p className="text-sm text-muted-foreground">Family Wallet</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {editingWallet?.id !== wallet.id && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => startEditingWallet(wallet)}
                                className="h-8 w-8"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => deleteWallet(wallet.id)}
                                className="h-8 w-8 text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {editingWallet?.id === wallet.id ? (
                        <div className="space-y-3">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="Wallet name"
                            className="w-full"
                          />
                          <Input
                            type="number"
                            value={editBalance}
                            onChange={(e) => setEditBalance(e.target.value)}
                            placeholder="Balance"
                            className="w-full"
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={saveWalletEdit} className="flex-1">
                              <Save className="h-4 w-4 mr-1" />
                              Save
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingWallet(null)} className="flex-1">
                              <X className="h-4 w-4 mr-1" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-2xl font-bold">
                            Rp {wallet.balance.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                          <div className="flex items-center gap-2">
                            <div className={`h-2 w-2 rounded-full ${wallet.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                            <p className="text-sm text-muted-foreground">
                              {wallet.is_active ? 'Active' : 'Inactive'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {editingWallet?.id !== wallet.id && (
                      <div className="mt-4 pt-4 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleActiveWallet(wallet.id)}
                          className="w-full"
                        >
                          {wallet.is_active ? 'Deactivate' : 'Activate'} Wallet
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
          </div>
        </div>

        {/* Business Wallets */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Business Wallets</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {wallets
              .filter(wallet => wallet.type === 'business')
              .map((wallet) => (
                <Card key={wallet.id} className="p-4 sm:p-6 hover:shadow-lg transition-shadow">
                  <div className="flex flex-col h-full">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-primary/10 rounded-full">
                            <Building2 className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-medium text-lg">{wallet.name}</h3>
                            <p className="text-sm text-muted-foreground">Business Wallet</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {editingWallet?.id !== wallet.id && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => startEditingWallet(wallet)}
                                className="h-8 w-8"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => deleteWallet(wallet.id)}
                                className="h-8 w-8 text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {editingWallet?.id === wallet.id ? (
                        <div className="space-y-3">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="Wallet name"
                            className="w-full"
                          />
                          <Input
                            type="number"
                            value={editBalance}
                            onChange={(e) => setEditBalance(e.target.value)}
                            placeholder="Balance"
                            className="w-full"
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={saveWalletEdit} className="flex-1">
                              <Save className="h-4 w-4 mr-1" />
                              Save
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingWallet(null)} className="flex-1">
                              <X className="h-4 w-4 mr-1" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-2xl font-bold">
                            Rp {wallet.balance.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                          <div className="flex items-center gap-2">
                            <div className={`h-2 w-2 rounded-full ${wallet.is_active ? 'bg-green-500' : 'bg-red-500'}`} />
                            <p className="text-sm text-muted-foreground">
                              {wallet.is_active ? 'Active' : 'Inactive'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {editingWallet?.id !== wallet.id && (
                      <div className="mt-4 pt-4 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleActiveWallet(wallet.id)}
                          className="w-full"
                        >
                          {wallet.is_active ? 'Deactivate' : 'Activate'} Wallet
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
          </div>
        </div>
      </div>

      {/* Add Wallet Modal */}
      {isAddingWallet && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md p-4 sm:p-6">
            <h2 className="text-lg font-medium mb-4">Add New Wallet</h2>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="walletName">Wallet Name</Label>
                <Input
                  id="walletName"
                  value={newWalletName}
                  onChange={(e) => setNewWalletName(e.target.value)}
                  placeholder="Enter wallet name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="walletBalance">Initial Balance</Label>
                <Input
                  id="walletBalance"
                  type="number"
                  value={newWalletBalance}
                  onChange={(e) => setNewWalletBalance(e.target.value)}
                  placeholder="Enter initial balance"
                  step="0.01"
                  min="0"
                />
              </div>
              <WalletTypeSelection
                onTypeSelect={(type) => setAccountType(type)}
                selectedType={accountType as 'personal' | 'family' | 'business' | undefined}
              />
              <div className="flex flex-col sm:flex-row justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsAddingWallet(false);
                    setNewWalletName('');
                    setNewWalletBalance('0');
                  }}
                  className="w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button onClick={addWallet} className="w-full sm:w-auto">
                  Add Wallet
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Wallets; 